import type { ValidationError } from '@flash-global66/g-attach-file';
import type { FileValidationResult } from '../types/fileValidation.type';
import {
  EMPTY_FILE_MESSAGE_PLURAL,
  EMPTY_FILE_MESSAGE_SINGULAR,
  FILE_VALIDATION_CONSTANTS,
  FILE_VALIDATION_MESSAGES,
} from '../constants/fileValidation.constant';

/**
 * Indica si un archivo no tiene contenido, comparando su tamaño contra cero.
 * @param file - Archivo a evaluar
 * @returns true si el archivo pesa 0 bytes, false si tiene contenido o no existe
 */
export function isEmptyFile(file: File | null | undefined): boolean {
  return file?.size === 0;
}

/**
 * Valida que ningún archivo de la lista esté vacío.
 * No lee el contenido, por lo que no detecta archivos corruptos que sí tienen
 * bytes: esa validación solo puede ocurrir en el backend.
 * Una lista sin archivos se considera válida, porque exigir adjuntos es
 * responsabilidad de la regla de obligatoriedad, no de esta.
 * El mensaje no enumera los nombres porque cada archivo se marca por separado
 * en la lista; los nombres se devuelven aparte para las pantallas que no
 * muestran esa lista y solo disponen de un mensaje suelto.
 * @param files - Archivos seleccionados por el usuario
 * @returns Resultado con el mensaje a mostrar y los nombres de los archivos vacíos
 */
export function nonEmptyFiles(
  files: File[] | null | undefined
): FileValidationResult {
  const emptyFileNames = (files ?? [])
    .filter(isEmptyFile)
    .map((file) => file.name);

  if (!emptyFileNames.length) {
    return { isValid: true, errorMessage: null, emptyFileNames: [] };
  }

  const errorMessage =
    emptyFileNames.length === 1
      ? EMPTY_FILE_MESSAGE_SINGULAR
      : EMPTY_FILE_MESSAGE_PLURAL;

  return { isValid: false, errorMessage, emptyFileNames };
}

/** Traduce un rechazo de `GAttachFile` (tamaño, formato o cantidad) al copy de validación de archivos. */
export function getFileValidationMessage(error: ValidationError): string {
  const fileName =
    error.file?.name || FILE_VALIDATION_CONSTANTS.UNKNOWN_FILE_NAME;

  switch (error.type) {
    case 'file-size-exceeded': {
      const actualSizeBytes = error.data?.actualSize || error.file?.size || 0;
      const sizeMB = (
        actualSizeBytes / FILE_VALIDATION_CONSTANTS.BYTES_PER_MB
      ).toFixed(2);
      const maxSize =
        error.data?.maxSize || FILE_VALIDATION_CONSTANTS.DEFAULT_MAX_SIZE;
      return FILE_VALIDATION_MESSAGES.SIZE_EXCEEDED(fileName, sizeMB, maxSize);
    }
    case 'invalid-file-type': {
      const validTypes =
        error.data?.acceptedExtensions?.join(', ') ||
        FILE_VALIDATION_CONSTANTS.VALID_EXTENSIONS.join(', ');
      return FILE_VALIDATION_MESSAGES.INVALID_TYPE(fileName, validTypes);
    }
    case 'max-files-exceeded': {
      const maxFiles =
        error.data?.maxFiles || FILE_VALIDATION_CONSTANTS.MAX_FILES_DEFAULT;
      const attemptedFiles = error.data?.attemptedFiles || 0;
      return attemptedFiles > 0
        ? FILE_VALIDATION_MESSAGES.MAX_FILES(maxFiles, attemptedFiles)
        : FILE_VALIDATION_MESSAGES.MAX_FILES_SIMPLE(maxFiles);
    }
    default:
      return FILE_VALIDATION_MESSAGES.GENERIC;
  }
}
